import React, { useState, useEffect } from 'react';
import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Box,
    Skeleton,
    Chip,
    Button,
    Tooltip,
    ButtonGroup,
    IconButton,
    TextField,
    InputAdornment
} from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import PrintIcon from '@mui/icons-material/Print';
import PreviewIcon from '@mui/icons-material/Preview';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { DrfData, DadosDemonstrativo, CfopDePara } from './types';
import { usePrintDrf } from '../../Hooks/usePrintDrf';
import DrfPrintPreviewModal from '../../Components/DrillDown/DrfPrintPreviewModal';
import { useDrfTaxUpdate } from '../../Hooks/useDrfTaxUpdate';

interface DrfHierarchicalTableProps {
    data: DrfData | null;
    loading: boolean;
    title: string;
    establishmentId: number | string | null;
    dataInicio?: string;
    dataFim?: string;
    onTaxValuesUpdate?: () => void;
}

const DrfHierarchicalTable: React.FC<DrfHierarchicalTableProps> = ({ 
    data, 
    loading, 
    title,
    establishmentId,
    dataInicio,
    dataFim,
    onTaxValuesUpdate
}) => {
    const { handlePrint, isPrintReady } = usePrintDrf({ data, title });
    const { updateTaxValues, loading: updateLoading } = useDrfTaxUpdate();
    const [showPrintPreview, setShowPrintPreview] = useState(false);
    const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());
    
    // State for editable PIS and COFINS values
    const [pisValue, setPisValue] = useState<string>('');
    const [cofinsValue, setCofinsValue] = useState<string>('');
    const [isEditing, setIsEditing] = useState(false);

    // Update input values when data changes
    useEffect(() => {
        if (data && data.tributos_vendas) {
            // Initialize with current values from tributos_vendas, defaulting to 0 and always showing
            setPisValue((data.tributos_vendas.pis ?? 0).toFixed(2).replace('.', ','));
            setCofinsValue((data.tributos_vendas.cofins ?? 0).toFixed(2).replace('.', ','));
        }
    }, [data]);

    const handleSaveTaxValues = async () => {
        if (!establishmentId || !dataInicio || !dataFim) {
            console.error('Dados necessários para atualização não disponíveis');
            return;
        }

        // Converte valores para número, substituindo vírgula por ponto
        const pisValueNum = parseFloat(pisValue.replace(',', '.'));
        const cofinsValueNum = parseFloat(cofinsValue.replace(',', '.'));

        const result = await updateTaxValues({
            establishment_id: establishmentId,
            data_inicio: dataInicio,
            data_fim: dataFim,
            pis_value: pisValueNum,
            cofins_value: cofinsValueNum
        });

        if (result.success) {
            setIsEditing(false);
            // Chama o callback para recarregar os dados, se fornecido
            onTaxValuesUpdate?.();
        }
    };

    const handleOpenPreview = () => {
        if (isPrintReady && data) {
            setShowPrintPreview(true);
        }
    };

    const handleClosePreview = () => {
        setShowPrintPreview(false);
    };

    const toggleGroupExpansion = (groupIndex: number) => {
        const newExpandedGroups = new Set(expandedGroups);
        if (newExpandedGroups.has(groupIndex)) {
            newExpandedGroups.delete(groupIndex);
        } else {
            newExpandedGroups.add(groupIndex);
        }
        setExpandedGroups(newExpandedGroups);
    };
    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(value);
    };

    const formatPercentage = (value: number, total: number) => {
        if (total === 0) return '0,00%';
        return ((value / total) * 100).toFixed(2).replace('.', ',') + '%';
    };

    const getRowStyle = (isTotal: boolean, isSubtotal: boolean, isNegative: boolean, colorIsTotal: string = '#f5f5f5') => ({
        backgroundColor: isTotal ? colorIsTotal  : isSubtotal ? '#fafafa' : 'transparent',
        fontWeight: isTotal ? 'bold' : isSubtotal ? '600' : 'normal',
        borderTop: isTotal ? '2px solid #ddd' : 'none',
        color: isNegative ? '#d32f2f' : 'inherit'
    });

    const renderTableRows = () => {
        if (!data) return null;
        const rows: React.ReactElement[] = [];

        // Adiciona os dados do demonstrativo
        (data?.dados_demonstrativo || []).forEach((grupo: DadosDemonstrativo, grupoIndex: number) => {
            // Verifica se o grupo é de tributos incidentes sobre as vendas
            const isTributosIncidentes = grupo.grouping_drf_data.description === '(-) TRIBUTOS INCIDENTES SOBRE AS VENDAS';

            if (['adoptedSon', 'childfree'].includes(grupo.grouping_drf_data?.type || '')) {
                let valorTotalGrupoEspecial = grupo.grouping_drf_data?.value;
                let percentualGrupoEspecial = grupo.grouping_drf_data?.percentual;
                
                rows.push(<TableRow 
                    key={`grupo-${grupoIndex}`} 
                    sx={{
                        ...getRowStyle(
                            true,
                            false,
                            (isNaN(Number(valorTotalGrupoEspecial)) ? 0 : Number(valorTotalGrupoEspecial)) < 0,
                            '#e3f2fd'
                        ),
                        cursor: 'pointer',
                        '&:hover': {
                            backgroundColor: '#f0f0f0'
                        }
                    }}
                >
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '1.0em', paddingLeft: '8px' }}>
                        <Box display="flex" alignItems="center" sx={{marginLeft: '34px'}}>
                            {grupo.grouping_drf_data.description}
                        </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.0em' }}>
                        {valorTotalGrupoEspecial}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.0em' }}>
                        {percentualGrupoEspecial}
                    </TableCell>
                </TableRow>
                );
                
                if(grupo.grouping_drf_data?.type === 'childfree') {
                    return;
                }
            }

            // Calcula o valor total do grupo somando todos os cfop_de_para
            const valorTotalGrupo = grupo.cfop_de_para.reduce((sum: number, cfop: CfopDePara) => {
                return sum + parseFloat(cfop.valor_total_c170 || '0');
            }, 0);

            // Linha do grupo principal (grouping_description) - sempre mostra, mesmo com valor zero
            let isExpanded = expandedGroups.has(grupoIndex);

            if(grupo.grouping_drf_data?.type === 'adoptedSon') {
               isExpanded = true;
            }

            if (!['adoptedSon', 'childfree'].includes(grupo.grouping_drf_data?.type || '')) {
                // Cabeçalhos especiais para separar as seções de "Essa operação pode envolver: ..."
                if (grupo.grouping_drf_data.description === 'Essa operação pode envolver: Entrada de mercadorias para consumo próprio, trocas, anulações, entre outros.') {
                    rows.push(
                        <TableRow 
                            key={`titulo-outras-operacoes-entrada-${grupoIndex}`} 
                            sx={{
                                backgroundColor: '#FFF3E0',
                                fontWeight: 'bold',
                                borderTop: '2px solid #ddd'
                            }}
                        >
                            <TableCell colSpan={3} sx={{ 
                                textAlign: 'left', 
                                color: '#333333', 
                                fontWeight: 'bold', 
                                fontSize: '1.0em' 
                            }}>
                                OUTRAS OPERAÇÕES NÃO ESPECIFICADAS - CFOP 1949/2949
                            </TableCell>
                        </TableRow>
                    );
                }
                if (grupo.grouping_drf_data.description === 'Essa operação pode envolver: Saída de mercadorias para consumo próprio, trocas, anulações, entre outros.') {
                    rows.push(
                        <TableRow 
                            key={`titulo-outras-operacoes-saida-${grupoIndex}`} 
                            sx={{
                                backgroundColor: '#FFF3E0',
                                fontWeight: 'bold',
                                borderTop: '2px solid #ddd'
                            }}
                        >
                            <TableCell colSpan={3} sx={{ 
                                textAlign: 'left', 
                                color: '#333333', 
                                fontWeight: 'bold', 
                                fontSize: '1.0em' 
                            }}>
                                OUTRAS OPERAÇÕES NÃO ESPECIFICADAS - CFOP 5949/6949
                            </TableCell>
                        </TableRow>
                    );
                }
                rows.push(
                    <TableRow 
                        key={`grupo-${grupoIndex}`} 
                        sx={{
                            ...getRowStyle(true, false, valorTotalGrupo < 0),
                            cursor: 'pointer',
                            '&:hover': {
                                backgroundColor: '#f0f0f0'
                            }
                        }}
                        onClick={() => toggleGroupExpansion(grupoIndex)}
                    >
                        <TableCell sx={{ fontWeight: 'bold', fontSize: '1.0em', paddingLeft: '8px' }}>
                            <Box display="flex" alignItems="center">
                                <IconButton 
                                    size="small" 
                                    sx={{ 
                                        mr: 1, 
                                        p: 0.5,
                                        transition: 'transform 0.2s',
                                        transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)'
                                    }}
                                >
                                    <ChevronRightIcon fontSize="small" />
                                </IconButton>
                                {grupo.grouping_drf_data.description}
                            </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.0em' }}>
                            {formatCurrency(valorTotalGrupo)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.0em' }}>
                            {formatPercentage(valorTotalGrupo, data.receita_total)}
                        </TableCell>
                    </TableRow>
                );

                // Adiciona linha fixa "OPERAÇÕES SEM EFEITO FINANCEIRO" após a linha "DEVOLUÇÃO IMOBILIZADO"
                if (grupo.grouping_drf_data.description === 'DEVOLUÇÃO IMOBILIZADO') {
                    rows.push(
                        <TableRow 
                            key="operacoes-sem-efeito-financeiro" 
                            sx={{
                                backgroundColor: '#FFF3E0', // Laranja claro
                                fontWeight: 'bold',
                                borderTop: '2px solid #ddd'
                            }}
                        >
                            <TableCell colSpan={3} sx={{ 
                                textAlign: 'left', 
                                color: '#333333', // Preto
                                fontWeight: 'bold', 
                                fontSize: '1.0em' 
                            }}>
                                OPERAÇÕES SEM EFEITO FINANCEIRO
                            </TableCell>
                        </TableRow>
                    );
                }
            }

        // Adiciona as operações individuais (cfop_de_para) apenas se o grupo estiver expandido
            if (isExpanded) {
            const totalReceita = Number(data.receita_total || 0);
            // Agrupa cfops por descrição para consolidar linhas iguais (ex.: Bonificações Recebidas)
            const cfopsAgrupados = grupo.cfop_de_para.reduce((acc: Record<string, { cfop: CfopDePara; valor: number; percSomada: number }>, cfop) => {
                    const chave = cfop.operacao_fechamento_fiscal;
                const valor = parseFloat(cfop.valor_total_c170 || '0');
                const perc = totalReceita !== 0 ? parseFloat(((valor / totalReceita) * 100).toFixed(2)) : 0;
                    if (!acc[chave]) {
                    acc[chave] = { cfop, valor, percSomada: perc };
                    } else {
                        acc[chave].valor += valor;
                    acc[chave].percSomada += perc;
                    }
                    return acc;
                }, {});

            Object.values(cfopsAgrupados)
                // Ordena mantendo Energia Elétrica por último dentro do grupo
                .sort((a, b) => {
                    const aKey = a.cfop.operacao_fechamento_fiscal;
                    const bKey = b.cfop.operacao_fechamento_fiscal;
                    const isAEnergia = aKey?.includes('Energia El');
                    const isBEnergia = bKey?.includes('Energia El');
                    if (isAEnergia && !isBEnergia) return 1;
                    if (!isAEnergia && isBEnergia) return -1;
                    return (aKey || '').localeCompare(bKey || '', 'pt-BR');
                })
                .forEach(({ cfop, valor, percSomada }, cfopIndex) => {
                    const valorCfop = valor;
                const percCfop = totalReceita !== 0 ? percSomada : 0;
                const operacaoLabel = cfop.operacao_fechamento_fiscal === 'Energia ElÃ©trica'
                    ? 'Energia Elétrica'
                    : cfop.operacao_fechamento_fiscal;
                    
                    // Adiciona linha especial para PIS e COFINS com campos editáveis
                    if (isTributosIncidentes && 
                        (cfop.operacao_fechamento_fiscal === '(-) PIS' || 
                         cfop.operacao_fechamento_fiscal === '(-) COFINS')) {
                        rows.push(
                            <TableRow key={`cfop-${grupoIndex}-${cfopIndex}`} sx={getRowStyle(false, false, valorCfop < 0)}>
                                <TableCell sx={{ paddingLeft: '48px', fontSize: '0.9em' }}>
                                    <Box display="flex" alignItems="center">
                                        {operacaoLabel}
                                        {isEditing ? (
                                            <TextField
                                                variant="standard"
                                                size="small"
                                                sx={{ ml: 2, width: '100px' }}
                                                value={cfop.operacao_fechamento_fiscal === '(-) PIS' ? pisValue : cofinsValue}
                                                onChange={(e) => 
                                                    cfop.operacao_fechamento_fiscal === '(-) PIS' 
                                                        ? setPisValue(e.target.value) 
                                                        : setCofinsValue(e.target.value)
                                                }
                                                InputProps={{
                                                    startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                                                }}
                                            />
                                        ) : null}
                                    </Box>
                                </TableCell>
                                <TableCell align="right" sx={{ fontSize: '0.9em' }}>
                                    {formatCurrency(valorCfop)}
                                </TableCell>
                                <TableCell align="right" sx={{ fontSize: '0.9em' }}>
                                    {percCfop.toFixed(2).replace('.', ',') + '%'}
                                </TableCell>
                            </TableRow>
                        );
                    } else if (valorCfop !== 0) {
                        rows.push(
                            <TableRow key={`cfop-${grupoIndex}-${cfopIndex}`} sx={getRowStyle(false, false, valorCfop < 0)}>
                                <TableCell sx={{ paddingLeft: '48px', fontSize: '0.9em' }}>
                                    {operacaoLabel}
                                </TableCell>
                                <TableCell align="right" sx={{ fontSize: '0.9em' }}>
                                    {formatCurrency(valorCfop)}
                                </TableCell>
                                <TableCell align="right" sx={{ fontSize: '0.9em' }}>
                                    {percCfop.toFixed(2).replace('.', ',') + '%'}
                                </TableCell>
                            </TableRow>
                        );
                    }
                });
            }
        });

        return rows;
    };

    if (loading) {
        return (
            <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                    {title}
                </Typography>
                <Box>
                    {[...Array(10)].map((_, index) => (
                        <Skeleton key={index} height={40} sx={{ mb: 1 }} />
                    ))}
                </Box>
            </Paper>
        );
    }

    return (
        <Paper sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Box display="flex" alignItems="center">
                    <AssessmentIcon sx={{ mr: 1, fontSize: 32, color: '#1976d2' }} />
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {title}
                    </Typography>
                </Box>
                <Box display="flex" alignItems="center">
                    <Tooltip title={isPrintReady ? "Opções de impressão" : "Não há dados disponíveis para impressão"}>
                        <span>
                            <ButtonGroup 
                                variant="outlined" 
                                disabled={!isPrintReady || loading}
                                sx={{
                                    '& .MuiButton-root': {
                                        borderColor: '#1976d2',
                                        color: '#1976d2',
                                        '&:hover': {
                                            borderColor: '#1565c0',
                                            backgroundColor: 'rgba(25, 118, 210, 0.04)'
                                        },
                                        '&:disabled': {
                                            borderColor: '#ccc',
                                            color: '#ccc'
                                        }
                                    }
                                }}
                            >
                                <Button
                                    startIcon={<PreviewIcon />}
                                    onClick={handleOpenPreview}
                                    disabled={!isPrintReady || loading}
                                >
                                    Visualizar
                                </Button>
                                <Button
                                    startIcon={<PrintIcon />}
                                    onClick={handlePrint}
                                    disabled={!isPrintReady || loading}
                                >
                                    Imprimir
                                </Button>
                            </ButtonGroup>
                        </span>
                    </Tooltip>
                    
                    {/* Botões de edição de PIS e COFINS */}
                    {data && (
                        <Box ml={2}>
                            {!isEditing ? (
                                <Button 
                                    variant="outlined" 
                                    color="primary" 
                                    onClick={() => setIsEditing(true)}
                                    disabled={loading || updateLoading}
                                >
                                    Editar PIS/COFINS
                                </Button>
                            ) : (
                                <ButtonGroup variant="outlined">
                                    <Button 
                                        color="primary" 
                                        onClick={handleSaveTaxValues}
                                        disabled={loading || updateLoading}
                                    >
                                        Salvar
                                    </Button>
                                    <Button 
                                        color="secondary" 
                                        onClick={() => {
                                            setIsEditing(false);
                                            // Restaura os valores originais, com fallback para 0
                                            setPisValue((data.tributos_vendas?.pis ?? 0).toFixed(2).replace('.', ','));
                                            setCofinsValue((data.tributos_vendas?.cofins ?? 0).toFixed(2).replace('.', ','));
                                        }}
                                        disabled={loading || updateLoading}
                                    >
                                        Cancelar
                                    </Button>
                                </ButtonGroup>
                            )}
                        </Box>
                    )}
                </Box>
            </Box>
            
            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ backgroundColor: '#1976d2' }}>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold', width: '60%' }}>
                                Descrição
                            </TableCell>
                            <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold', width: '25%' }}>
                                Valor
                            </TableCell>
                            <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold', width: '15%' }}>
                                % sobre a receita
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {renderTableRows()}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Modal de Visualização Prévia */}
            {data && (
                <DrfPrintPreviewModal
                    open={showPrintPreview}
                    onClose={handleClosePreview}
                    data={data}
                    title={title}
                />
            )}
        </Paper>
    );
};

export default DrfHierarchicalTable;
